import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { DashboardAnimalData } from '../../models/dashboard';
import { ChartContainer, type ChartConfig } from '../../elements/ui/chart';
import { ChartHoverCard } from '../../elements/ui/hover-card';
import { colors } from '../../utils/colors';

/** One of the three vaccine slots a farm configures in dashboard settings, with its fixed colour. */
export interface VaccineSeries {
  dataKey: 'countA' | 'countB' | 'countC';
  name: string;
  color: string;
}

/**
 * Slot A always plots; B and C only once the farm has named them. The colour belongs to the slot, not
 * to how many slots are in use, so dropping an unconfigured C never repaints A and B.
 */
export const getVaccineSeries = (data: DashboardAnimalData): VaccineSeries[] =>
  [
    { dataKey: 'countA' as const, name: data.vaccineA, color: colors.barChartA },
    { dataKey: 'countB' as const, name: data.vaccineB, color: colors.barChartB },
    { dataKey: 'countC' as const, name: data.vaccineC, color: colors.barChartC }
  ].filter((series, index) => index === 0 || Boolean(series.name));

interface VaccineTooltipProps {
  active?: boolean;
  label?: number | string;
  payload?: { dataKey?: string | number; value?: number }[];
  series: VaccineSeries[];
}

/**
 * Lists every configured vaccine for the week, so a slot reading zero is visibly zero rather than absent.
 *
 * ChartHoverCard rather than a plain div because one row per vaccine easily makes this card taller than the
 * card chart's ~105px plot area, and recharts cannot clamp a tooltip that is taller than its viewBox - see
 * the note on ChartHoverCard.
 */
function VaccineTooltip({ active, label, payload, series }: VaccineTooltipProps) {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  return (
    <ChartHoverCard className="min-w-44">
      <p className="mb-1.5 font-medium text-gray-700">{t('f-dashboard-chart-week', { label })}</p>

      <div className="grid gap-1">
        {series.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-6">
            <span className="flex min-w-0 items-center gap-1.5 text-gray-500">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="truncate">{entry.name || '—'}</span>
            </span>
            <span className="shrink-0 font-medium tabular-nums text-gray-900">
              {payload.find((row) => row.dataKey === entry.dataKey)?.value ?? 0}
            </span>
          </div>
        ))}
      </div>
    </ChartHoverCard>
  );
}

/**
 * Standalone legend. It lives outside the plot so the vaccine names can wrap over as many lines as they
 * need without eating chart height, and so the names are readable without hovering.
 */
export function VaccineLegend({ series }: { series: VaccineSeries[] }) {
  const named = series.filter((entry) => Boolean(entry.name));

  if (named.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-4 text-gray-600">
      {named.map((entry) => (
        <span key={entry.dataKey} className="flex min-w-0 items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: entry.color }} />
          <span className="truncate" title={entry.name}>
            {entry.name}
          </span>
        </span>
      ))}
    </div>
  );
}

export interface DashboardAnimalDataChartProps {
  data: DashboardAnimalData;
}

export default function DashboardAnimalDataChart({ data }: DashboardAnimalDataChartProps) {
  const { t } = useTranslation();

  const series = getVaccineSeries(data);
  const chartConfig: ChartConfig = Object.fromEntries(
    series.map((entry) => [entry.dataKey, { label: entry.name, color: entry.color }])
  );

  if (!data.chartData?.length) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-gray-400">
        {t('f-dashboard-chart-empty')}
      </div>
    );
  }

  return (
    <ChartContainer config={chartConfig} resizeDebounce={250} className="h-full w-full">
      {/* Negative left margin pulls the plot back over the Y-axis gutter - these cards are narrow and
          the axis labels are short, so the default gutter is wasted width. */}
      <BarChart data={data.chartData} margin={{ top: 8, right: 6, bottom: 0, left: -18 }} barGap={2}>
        <CartesianGrid vertical={false} stroke="#f3f4f6" />

        <XAxis
          dataKey="weekNumber"
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          minTickGap={2}
          interval="preserveStartEnd"
          tick={{ fontSize: 11, fill: '#9ca3af' }}
        />
        <YAxis
          allowDecimals={false}
          width={34}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
        />

        {/* isAnimationActive: the wrapper the card measures itself against has to jump to the cursor, not
            glide there. See ChartHoverCard. */}
        <RechartsTooltip
          cursor={{ fill: '#f9fafb' }}
          isAnimationActive={false}
          content={<VaccineTooltip series={series} />}
        />

        {series.map((entry) => (
          <Bar
            key={entry.dataKey}
            dataKey={entry.dataKey}
            name={entry.name}
            fill={entry.color}
            radius={[3, 3, 0, 0]}
            maxBarSize={9}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}
